import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { lazyGettext } from '@eb/lazy-gettext';
import includes from 'lodash/includes';

import { Button } from '@eb/eds-button';
import { Icon } from '@eb/eds-icon';
import { STYLE_FILL, SIZE_BLOCK } from '@eb/eds-button';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK } from './constants';
import FacebookLoginComponent from './FacebookLoginComponent';
import './facebookLoginButton.scss';

import { LogoFacebookWhite } from '@eb/eds-iconography';

const FacebookLogo = () => (
    <div className="facebook-button__logo eds-g-cell">
        <div className="eds-align--left">
            <Icon type={<LogoFacebookWhite />} size="small" />
        </div>
    </div>
);

const ContinueWithFacebookButton = ({
    accessToken,
    picture,
    firstName,
    handleOnClick,
}) => {
    let component = null;
    const dataAutomation = 'facebook-login-button';
    let pictureUrl = '';

    if (picture && picture.data && picture.data.url) {
        pictureUrl = picture.data.url;
    }

    if (accessToken) {
        component = (
            <div className="facebook-button-container">
                <Button
                    data-automation={dataAutomation}
                    type="button"
                    style={STYLE_FILL}
                    size={SIZE_BLOCK}
                    onClick={handleOnClick}
                >
                    <div className="eds-g-grid">
                        <div className="eds-g-group eds-g-group--full-width">
                            <div className="facebook-button__content eds-align--center">
                                <FacebookLogo />

                                <div className="facebook-button__text eds-g-cell">
                                    {lazyGettext('Continue as %(firstName)s ', {
                                        firstName,
                                    })}
                                </div>
                                <div className="eds-g-cell facebook-button__image-wrapper">
                                    <div className="eds-align--center">
                                        <img
                                            className="facebook-button__image"
                                            src={pictureUrl}
                                            alt={pictureUrl}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Button>
            </div>
        );
    } else {
        component = (
            <div className="facebook-button-container">
                <Button
                    data-automation={dataAutomation}
                    type="button"
                    style={STYLE_FILL}
                    size={SIZE_BLOCK}
                    onClick={handleOnClick}
                >
                    <div className="eds-g-grid eds-g-group eds-g-group--full-width">
                        <div className="facebook-button__content eds-align--center">
                            <FacebookLogo />

                            <div className="facebook-button__text eds-g-cell eds-l-pad-hor-1">
                                {lazyGettext('Continue with Facebook')}
                            </div>
                        </div>
                    </div>
                </Button>
            </div>
        );
    }
    return component;
};

export default class FacebookLoginButton extends Component {
    static propTypes = {
        appId: PropTypes.string.isRequired,
        version: PropTypes.string,
        scope: PropTypes.string,
        fields: PropTypes.string,
        onSocialConnectVerified: PropTypes.func,
        onSocialConnectNotAuthorized: PropTypes.func,
        onClick: PropTypes.func,
        isCheckoutAuthenticationFlow: PropTypes.bool,
        handleOnLoadComplete: PropTypes.func,
        handleOnNotConnected: PropTypes.func,
        handleOnComponentUpdated: PropTypes.func,
    };

    static defaultProps = {
        fields: 'name,email,picture,first_name,last_name',
    };

    state = {
        transitToLogin: false,
    };

    /**
       Called when the FacebookLoginComponent performs the autologin and the user is not logged
       on Facebook, in this scenario we should activate the transitToLogin state so following calls to
       handleResponse will start the login process
    */
    _onFacebookUserNotConnected = (status) => {
        const {
            onSocialConnectNotAuthorized,
            handleOnNotConnected,
        } = this.props;

        if (handleOnNotConnected) {
            handleOnNotConnected();
        } else {
            if (
                this.state.transitToLogin &&
                includes(['unknown', 'not_authorized'], status)
            ) {
                if (onSocialConnectNotAuthorized) {
                    onSocialConnectNotAuthorized({
                        sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                        status,
                    });
                }
            }

            this.setState({
                status,
                transitToLogin: true,
            });
        }
    };

    /**
       Called after the FacebookLoginComponent get a success response from the Facebook login call, this method can be
       invoked either after the autologin or when the user clicks on Continue with Facebook button. It should only transit to
       login if we can ensure the user has clicked the button.
     */
    _onFacebookResponse = (response) => {
        const { onSocialConnectVerified } = this.props;
        const { transitToLogin } = this.state;
        const {
            accessToken,
            email,
            first_name: firstName,
            last_name: lastName,
            picture: { data: { url: userPicture } = {} } = {},
        } = response;

        if (transitToLogin) {
            if (onSocialConnectVerified) {
                onSocialConnectVerified({
                    accessToken,
                    email,
                    sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                    firstName,
                    lastName,
                    userPicture,
                });
            }
        } else {
            this.setState({
                ...response,
                transitToLogin: true,
            });
        }
    };

    render() {
        const {
            appId,
            version,
            scope,
            fields,
            reRequestPermissions,
            handleOnLoadComplete,
            handleOnComponentUpdated,
            isCheckoutAuthenticationFlow,
            onClick: onButtonClick,
        } = this.props;

        const {
            accessToken,
            picture,
            first_name: firstName,
            ...state
        } = this.state;

        const renderButton = ({ onClick }) => {
            const _handleOnClick = () => {
                if (onButtonClick) {
                    const socialProvider = SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK;
                    onButtonClick(state, socialProvider);
                }
                onClick();
            };

            return (
                <ContinueWithFacebookButton
                    accessToken={accessToken}
                    picture={picture}
                    firstName={firstName}
                    handleOnClick={_handleOnClick}
                />
            );
        };

        return (
            <FacebookLoginComponent
                appId={appId}
                onSocialConnectVerified={this.props.onSocialConnectVerified}
                handleOnResponse={this._onFacebookResponse}
                handleOnLoadComplete={handleOnLoadComplete}
                handleOnComponentUpdated={handleOnComponentUpdated}
                handleOnNotConnected={this._onFacebookUserNotConnected}
                isCheckoutAuthenticationFlow={isCheckoutAuthenticationFlow}
                renderProps={renderButton}
                version={version}
                autoLogin={true}
                scope={scope}
                fields={fields}
                cookie={true}
                reRequestPermissions={reRequestPermissions}
            />
        );
    }
}
